import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { PageLoading } from '@cloudflare-monorepo/ui/components/PageLoading.tsx';
import { waitForMockDelay } from '@cloudflare-monorepo/ui/services/mock.ts';
import { CriticalFailure } from '@cloudflare-monorepo/ui/components/CriticalFailure.tsx';
import { Wrap } from '@cloudflare-monorepo/utils/errors';

import * as Sentry from '@sentry/react';
import { useLogger } from '@cloudflare-monorepo/ui/providers/LoggerProvider.tsx';
import { Logger } from '@cloudflare-monorepo/utils/logging/logger';
import { parseRequestError } from '../services/api.ts';
import { initializeI18nWithUserLanguage } from '../i18n/config';
import { AccessLevelEnum, GetAccountDetailsResponse, PermissionEnum } from '../../generated/account';
import { ApiClient as AccountApiClient } from '../../generated/account/apiClient.ts';
import { buildFetchClient } from './ApiClientProvider.tsx';

export type LocalPrincipalGroup = {
  id: string;
  internalIdentifier: string;
};

export type EnrichedUser = GetAccountDetailsResponse;

type AuthContextType = {
  user: EnrichedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithRedirect: () => Promise<void>;
  logout: (p?: { returnTo?: string }) => void;
  permissions: { [key in PermissionEnum]: true };
  accessLevels: { [key in AccessLevelEnum]: true };
  hasPermissions: (...required: PermissionEnum[]) => boolean;
  hasAnyPermission: (...required: PermissionEnum[]) => boolean;
  triggerForcedRefresh: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }) => {
  const logger = useLogger('auth');
  const [enrichedUser, setEnrichedUser] = useState<EnrichedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const [forceRefresh, setForceRefresh] = useState(0);

  useEffect(() => {
    let closed = false;

    const fetchUser = async () => {
      setIsLoading(true);
      try {
        const accountApi = new AccountApiClient(buildFetchClient(logger));

        await waitForMockDelay();
        const accountResponse = await accountApi.getAccount();
        logger.debug('remote accountResponse', accountResponse);

        if (!closed) {
          setEnrichedUser(accountResponse);

          if (import.meta.env.VITE_SENTRY_DSN) {
            Sentry.setUser({
              id: accountResponse.userId,
              username: accountResponse.email,
              email: accountResponse.email,
            });
          }

          checkAndUpdateNotificationsAsync(accountApi, accountResponse, () => closed, logger);

          if (accountResponse.language) {
            await initializeI18nWithUserLanguage(accountResponse.language);
          }
        }
      } catch (error) {
        logger.error('Error fetching user account', { error });

        const asRequestError = await parseRequestError(error);
        if (asRequestError.statusCode === 401) {
          // CF Access should prevent this, but if it happens redirect to login
          loginWithRedirect();
          return;
        }
        if (asRequestError.isApplicationError) {
          if (asRequestError.statusCode === 403) {
            setError(Wrap(error, 'Accesso negato: ' + (asRequestError.applicationError?.error || 'errore sconosciuto')));
          } else {
            setError(Wrap(error, "Non sono riuscito a caricare i dati dell'utente"));
          }
        } else {
          setError(Wrap(error, "Non sono riuscito a caricare i dati dell'utente"));
        }
      } finally {
        if (!closed) {
          setIsLoading(false);
        }
      }
    };

    fetchUser();

    return () => {
      closed = true;
    };
  }, [forceRefresh]);

  const loginWithRedirect = async () => {
    window.location.reload();
  };

  const logout = (p?: { returnTo?: string }) => {
    const teamDomain = import.meta.env.VITE_CF_ACCESS_TEAM_DOMAIN;
    if (teamDomain) {
      const returnTo = encodeURIComponent(p?.returnTo ?? window.location.origin);
      window.location.href = `https://${teamDomain}/cdn-cgi/access/logout?returnTo=${returnTo}`;
    } else {
      // Local dev: just reload
      window.location.reload();
    }
  };

  const isAuthenticated = !!enrichedUser;

  const permissions = Object.fromEntries((enrichedUser?.permissions || []).map((k) => [k, true])) as { [key in PermissionEnum]: true };
  const accessLevels = Object.fromEntries((enrichedUser?.accessLevels || []).map((k) => [k, true])) as { [key in AccessLevelEnum]: true };

  const hasPermissions = useCallback(
    (...required: PermissionEnum[]) => {
      for (const r of required) {
        if (!permissions || !permissions[r]) return false;
      }
      return true;
    },
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (...required: PermissionEnum[]) => {
      if (!required.length) return true;
      for (const r of required) {
        if (!!permissions && permissions[r]) return true;
      }
      return false;
    },
    [permissions],
  );

  const triggerForcedRefresh = () => {
    setForceRefresh((c) => c + 1);
  };

  return (
    <AuthContext.Provider
      value={{
        user: enrichedUser,
        isAuthenticated,
        isLoading,
        loginWithRedirect,
        logout,
        permissions,
        accessLevels,
        hasPermissions,
        hasAnyPermission,
        triggerForcedRefresh,
      }}
    >
      {error ? (
        <CriticalFailure err={error} />
      ) : isLoading ? (
        <PageLoading details="verifica dell'utente" />
      ) : !enrichedUser ? (
        <CriticalFailure err={'accesso fallito'} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useMustHaveAuth = (): MustHaveAuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useMustHaveAuth must be used within an AuthProvider');
  }
  if (!context.user) {
    throw new Error('useMustHaveAuth must be used within an AuthProvider with a valid user');
  }
  return {
    user: context.user,
    loginWithRedirect: context.loginWithRedirect,
    logout: context.logout,
    permissions: context.permissions,
    accessLevels: context.accessLevels,
    hasPermission: context.hasPermissions,
    hasAnyPermission: context.hasAnyPermission,
    triggerForcedRefresh: context.triggerForcedRefresh,
  };
};

type MustHaveAuthContextType = {
  user: EnrichedUser;
  loginWithRedirect: () => Promise<void>;
  logout: (p?: { returnTo?: string }) => void;
  permissions: { [key in PermissionEnum]: true };
  accessLevels: { [key in AccessLevelEnum]: true };
  hasPermission: (...required: PermissionEnum[]) => boolean;
  hasAnyPermission: (...required: PermissionEnum[]) => boolean;
  triggerForcedRefresh: () => void;
};

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return new Uint8Array([...rawData].map((char) => char.charCodeAt(0)));
}

function checkAndUpdateNotificationsAsync(accountApi: AccountApiClient, user: EnrichedUser, isClosed: () => boolean, logger: Logger) {
  try {
    checkAndUpdateNotifications(accountApi, user, isClosed, logger).then(
      () => {
        logger.debug('checkAndUpdateNotificationsAsync: success');
      },
      (err) => {
        logger.error('checkAndUpdateNotificationsAsync: promise failed with error', { error: err });
      },
    );
  } catch (err) {
    logger.error('checkAndUpdateNotificationsAsync: synchronous error', { error: err });
  }
}

async function checkAndUpdateNotifications(accountApi: AccountApiClient, user: EnrichedUser, isClosed: () => boolean, logger: Logger) {
  const publicKey = import.meta.env.VITE_WEBPUSH_PUBLIC_KEY;
  if (!publicKey) {
    logger.warn('No public key for WebPush, skipping');
    return;
  }

  if ('serviceWorker' in navigator && 'PushManager' in window) {
    const registration = await navigator.serviceWorker.register('/sw.js');

    let existing = await registration.pushManager.getSubscription();
    if (existing) {
      if (!applicationServerKeysMatch(existing, publicKey)) {
        logger.warn('subscription endpoint changed, invalidating current subscription');
        try {
          const serialized = existing.toJSON();
          await existing.unsubscribe();
          existing = null;
          if (serialized.endpoint) {
            await accountApi.deleteAccountWebpush({
              subscription: { ...serialized, endpoint: serialized.endpoint },
            });
          }
        } catch (err) {
          logger.error('failed to delete old subscription on server', { error: err });
        }
      }
    }

    if (!existing) {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        logger.warn('Notification permission denied');
        return;
      }
      existing = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    if (isClosed()) return;

    const serialized = existing.toJSON();
    if (!serialized.endpoint) throw new Error('No endpoint in subscription');

    await accountApi.postAccountWebpush({
      subscription: { ...serialized, endpoint: serialized.endpoint },
      publicKey,
    });
  }
}

function applicationServerKeysMatch(existing: PushSubscription, expectedBase64Url: string): boolean {
  if (!existing.options.applicationServerKey) return false;
  const existingKey = new Uint8Array(existing.options.applicationServerKey as ArrayBuffer);
  const expectedKey = urlBase64ToUint8Array(expectedBase64Url);
  if (existingKey.length !== expectedKey.length) return false;
  for (let i = 0; i < existingKey.length; i++) {
    if (existingKey[i] !== expectedKey[i]) return false;
  }
  return true;
}
